import {
  jsonPointerGet,
  parseToolCallParamBindingRef,
  splitTemplate,
} from '@cotera/utilities';

/**
 * How a node addresses a field of its upstream output.
 *
 * Two forms, shared by every node that reads a single field rather than the
 * whole upstream value (filter conditions, debounce keys, bucket accumulate
 * paths):
 *
 * - a bare RFC 6901 JSON Pointer (`/status`, `''` for the whole value), read
 *   from the direct upstream output; or
 * - a node-scoped reference (`NodeName/pointer`), read from that ancestor's
 *   output — the same idiom tool-call param bindings use.
 *
 * Kept in its own module so the two readers cannot drift: a fix to how a
 * missing source resolves has to land once.
 */

/**
 * Normalizes an authored field path. The editors emit `{{/field}}` template
 * tokens from their pointer pickers, so a lone ref token unwraps to its
 * pointer; everything else is passed through trimmed.
 */
export function normalizeUpstreamFieldPath(path: string): string {
  const trimmed = path.trim();
  if (trimmed === '') {
    return '';
  }
  const segments = splitTemplate(trimmed);
  if (segments.length === 1 && segments[0]?.t === 'ref') {
    return segments[0].ref;
  }
  return trimmed;
}

/**
 * True when a path names an upstream node to read from (`NodeName/pointer`)
 * rather than addressing the direct upstream output (`/field`).
 */
export function isNodeScopedFieldPath(path: string): boolean {
  if (path === '' || path.startsWith('/')) {
    return false;
  }
  try {
    return parseToolCallParamBindingRef(path).sourceName !== undefined;
  } catch {
    return false;
  }
}

/**
 * Reads the value a path addresses. A bare pointer reads the direct upstream
 * value; a `NodeName/pointer` reference reads that ancestor's output from
 * `upstreamByNodeId`.
 *
 * Never throws — a missing source or a pointer that does not resolve yields
 * `undefined`, so callers decide what an absent field means rather than having
 * a malformed row fail the step.
 */
export function resolveUpstreamFieldValue(params: {
  upstream: unknown;
  path: string;
  upstreamByNodeId: Readonly<Record<string, unknown>> | undefined;
}): unknown {
  const { upstream, path, upstreamByNodeId } = params;
  if (!isNodeScopedFieldPath(path)) {
    return jsonPointerGet(upstream, path);
  }
  const { sourceName, pointer } = parseToolCallParamBindingRef(path);
  if (sourceName === undefined) {
    return jsonPointerGet(upstream, path);
  }
  const root = upstreamByNodeId?.[sourceName];
  if (root === undefined) {
    return undefined;
  }
  return jsonPointerGet(root, pointer);
}

/**
 * True when a path is addressable at all — empty (the whole upstream value), a
 * JSON Pointer, or a node-scoped reference. The shared validation behind every
 * "path must be a JSON Pointer or an upstream reference" error.
 */
export function isAddressableUpstreamFieldPath(path: string): boolean {
  return path === '' || path.startsWith('/') || isNodeScopedFieldPath(path);
}
